import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  ScanLine, 
  X, 
  Camera, 
  Package, 
  MapPin, 
  History,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Plus,
  Trash2,
  ListChecks,
  RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';

const BarcodeScanner = ({ 
  isOpen, 
  onClose, 
  onScanSuccess, 
  onBatchScanSuccess,
  scanMode = 'equipment',
  showHistory = true,
  autoAdd = false,
  batchMode = false
}) => {
  const { api } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [entityDetails, setEntityDetails] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [batchItems, setBatchItems] = useState([]);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const manualInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      startScanner();
      setBatchItems([]);
    }
    return () => { stopScanner(); };
  }, [isOpen]);

  const isNative = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();

  const startScanner = async () => {
    if (html5QrCodeRef.current) return;
    try {
      setScanning(true);
      setError(null);
      
      // Try native scanning on mobile (Capacitor)
      if (isNative) {
        try {
          const { BarcodeScanner: NativeScanner } = await import('@capacitor-mlkit/barcode-scanning');
          const { supported } = await NativeScanner.isSupported();
          if (supported) {
            const { barcodes } = await NativeScanner.scan();
            if (barcodes.length > 0) {
              onScanDetected(barcodes[0].rawValue);
            }
            return;
          }
        } catch (nativeErr) {
          console.log("Native scanner not available, falling back to web:", nativeErr);
        }
      }
      
      // Web fallback with html5-qrcode
      const { Html5Qrcode } = await import('html5-qrcode');
      html5QrCodeRef.current = new Html5Qrcode("qr-reader");
      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        onScanDetected,
        () => {}
      );
    } catch (err) {
      console.error("Scanner error:", err);
      setError("No se pudo acceder a la cámara. Verifica los permisos o usa la entrada manual.");
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try { await html5QrCodeRef.current.stop(); } catch {}
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const lookupCode = useCallback(async (codeText) => {
    try {
      const res = await api().get(`/barcode/lookup/${encodeURIComponent(codeText)}`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Código no reconocido' };
    }
  }, [api]);

  const onScanDetected = async (decodedText) => {
    await stopScanner();
    setScannedData(decodedText);
    setLoading(true);
    
    if (scanMode === 'equipment' && decodedText.startsWith('LOC:')) {
      setError('Se esperaba un código de equipo, no de ubicación');
      setLoading(false);
      return;
    }
    if (scanMode === 'location' && !decodedText.startsWith('LOC:')) {
      setError('Se esperaba un código de ubicación');
      setLoading(false);
      return;
    }

    const result = await lookupCode(decodedText);
    
    if (result.success) {
      if (batchMode) {
        const isDuplicate = batchItems.some(item => 
          item.entity?.id === result.data.entity?.id
        );
        if (isDuplicate) {
          toast.warning('Este elemento ya fue escaneado');
        } else {
          setBatchItems(prev => [...prev, result.data]);
          toast.success(`Agregado: ${result.data.entity?.name || result.data.entity?.serial_number || 'Elemento'}`);
        }
        setScannedData(null);
        setLoading(false);
        setTimeout(() => startScanner(), 500);
        return;
      }
      setEntityDetails(result.data);
      if (autoAdd && onScanSuccess) {
        onScanSuccess(result.data);
        toast.success(`${result.data.type === 'equipment' ? 'Equipo' : result.data.type === 'unit' ? 'Unidad' : 'Ubicación'} escaneado`);
        handleClose();
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleManualLookup = async () => {
    const code = manualCode.trim();
    if (!code) return;
    
    setLoading(true);
    setError(null);
    
    const result = await lookupCode(code);
    
    if (result.success) {
      if (batchMode) {
        const isDuplicate = batchItems.some(item => item.entity?.id === result.data.entity?.id);
        if (isDuplicate) {
          toast.warning('Este elemento ya fue escaneado');
        } else {
          setBatchItems(prev => [...prev, result.data]);
          toast.success(`Agregado: ${result.data.entity?.name || result.data.entity?.serial_number || 'Elemento'}`);
        }
        setManualCode('');
        manualInputRef.current?.focus();
      } else {
        setEntityDetails(result.data);
        setScannedData(code);
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleConfirm = () => {
    if (entityDetails && onScanSuccess) {
      onScanSuccess(entityDetails);
      toast.success('Elemento agregado');
    }
    handleClose();
  };

  const handleBatchConfirm = () => {
    if (batchItems.length > 0 && onBatchScanSuccess) {
      onBatchScanSuccess(batchItems);
      toast.success(`${batchItems.length} elementos agregados`);
    } else if (batchItems.length > 0 && onScanSuccess) {
      batchItems.forEach(item => onScanSuccess(item));
      toast.success(`${batchItems.length} elementos agregados`);
    }
    handleClose();
  };

  const removeBatchItem = (index) => {
    setBatchItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    stopScanner();
    setScannedData(null);
    setEntityDetails(null);
    setError(null);
    setManualCode('');
    setBatchItems([]);
    onClose();
  };

  const handleRescan = () => {
    setScannedData(null);
    setEntityDetails(null);
    setError(null);
    startScanner();
  };

  const getEntityLabel = (type) => {
    switch (type) {
      case 'equipment': return 'Tipo de Equipo';
      case 'unit': return 'Unidad';
      case 'location': return 'Ubicación';
      default: return 'Desconocido';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" />
            {batchMode ? 'Escaneo Múltiple' : 'Escáner de códigos'}
            {batchMode && batchItems.length > 0 && (
              <Badge variant="secondary" className="ml-2">{batchItems.length}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Manual Input - Always visible */}
          <div className="flex gap-2" data-testid="manual-barcode-input">
            <Input
              ref={manualInputRef}
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleManualLookup()}
              placeholder="Ingresa serial o código de barras..."
              className="flex-1"
              data-testid="barcode-manual-input"
            />
            <Button 
              onClick={handleManualLookup} 
              disabled={loading || !manualCode.trim()}
              size="sm"
              data-testid="barcode-manual-search-btn"
            >
              Buscar
            </Button>
          </div>

          {/* Scanner View */}
          {!scannedData && !error && (
            <div className="relative">
              <div 
                id="qr-reader" 
                ref={scannerRef}
                className="rounded-lg overflow-hidden bg-black"
                style={{ minHeight: '250px' }}
              />
              {scanning && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-48 h-48 border-2 border-primary rounded-lg animate-pulse" />
                </div>
              )}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="text-center py-6">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">Buscando información...</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="text-center py-4">
              <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-2" />
              <p className="text-destructive font-medium text-sm mb-1">Error</p>
              <p className="text-xs text-muted-foreground mb-3">{error}</p>
              <Button onClick={handleRescan} variant="outline" size="sm">
                <RotateCcw className="h-3 w-3 mr-1" />
                Intentar de nuevo
              </Button>
            </div>
          )}

          {/* Single Scan Result */}
          {!batchMode && entityDetails && !loading && !error && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-primary/10 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                  {entityDetails.type === 'location' ? <MapPin className="h-4 w-4" /> : <Package className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <Badge variant="outline" className="mb-0.5 text-[10px]">{getEntityLabel(entityDetails.type)}</Badge>
                  <p className="font-medium text-sm truncate">
                    {entityDetails.entity?.name || entityDetails.entity?.serial_number || 'N/A'}
                  </p>
                  {entityDetails.equipment && (
                    <p className="text-xs text-muted-foreground truncate">{entityDetails.equipment.name}</p>
                  )}
                </div>
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                {entityDetails.type === 'unit' && (
                  <>
                    <div className="bg-muted/50 p-2 rounded">
                      <span className="text-muted-foreground">Serial:</span>
                      <span className="ml-1 font-medium">{entityDetails.entity?.serial_number || 'N/A'}</span>
                    </div>
                    <div className="bg-muted/50 p-2 rounded">
                      <span className="text-muted-foreground">Estado:</span>
                      <Badge variant="secondary" className="ml-1 text-[10px]">{entityDetails.entity?.status}</Badge>
                    </div>
                  </>
                )}
                {entityDetails.type === 'equipment' && (
                  <div className="bg-muted/50 p-2 rounded col-span-2">
                    <span className="text-muted-foreground">Unidades disponibles:</span>
                    <span className="ml-1 font-medium">{entityDetails.units?.filter(u => u.status === 'disponible').length || 0}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Batch Scan Results */}
          {batchMode && batchItems.length > 0 && (
            <div className="border rounded-lg">
              <div className="flex items-center gap-2 p-2 border-b bg-muted/30">
                <ListChecks className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Elementos escaneados ({batchItems.length})</span>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y">
                {batchItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`batch-item-${idx}`}>
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                      {item.type === 'location' ? <MapPin className="h-3 w-3" /> : <Package className="h-3 w-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-xs truncate">
                        {item.entity?.name || item.entity?.serial_number || 'N/A'}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {getEntityLabel(item.type)}
                        {item.equipment && ` - ${item.equipment.name}`}
                        {item.entity?.status && ` (${item.entity.status})`}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6 flex-shrink-0"
                      onClick={() => removeBatchItem(idx)}
                      data-testid={`remove-batch-item-${idx}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {batchMode ? (
            <>
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={handleBatchConfirm} 
                disabled={batchItems.length === 0}
                className="flex-1"
                data-testid="batch-confirm-btn"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Confirmar {batchItems.length} elemento{batchItems.length !== 1 ? 's' : ''}
              </Button>
            </>
          ) : entityDetails && !loading && !error ? (
            <>
              <Button variant="outline" onClick={handleRescan}>Escanear otro</Button>
              <Button onClick={handleConfirm} data-testid="confirm-scan-btn">Confirmar y agregar</Button>
            </>
          ) : (
            <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
